import { CroppedImage } from '@weee-fe/module';
import PlayerDialog from './player-dialog';
import useLazy from '@/hooks/useLazy';
import { CmsSsrComponentProps } from '@/types/cms';
import Container from '../Container';
import getContainerRatio from './get-container-ratio';
import { useIsMounted } from '@/hooks/use-is-mounted';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import AutoPlayVideo from './auto-play-video';
import { exitAnyPictureInPicture } from './use-picture-in-picture';

const HeroVideo = (props: Readonly<CmsSsrComponentProps>) => {
  const { componentKey, componentInstanceKey, properties, top, componentPosition, trackingContext } = props;

  const isMounted = useIsMounted();
  const controller = useLazy();
  const {
    mobile_background_image,
    mobile_foreground_image,
    video_url,
    mobileVideoInfo,
    event_key,
    video_play_in_popup
  } = properties as {
    background: string;
    event_key: string;
    horizontal_margin: string;
    mobile_background_image: string;
    mobile_foreground_image: string;
    pc_background_image: string;
    pc_foreground_image: string;
    vertical_margin: string;
    video_url: string;
    mobileVideoInfo: string;
    video_play_in_popup: 'no' | 'yes';
  };
  const containerRatio = getContainerRatio(mobileVideoInfo);
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();

  const isInteractive = video_play_in_popup === 'yes';
  const tabIndex = isInteractive ? 0 : -1;

  return (
    <>
      <Container
        componentKey={componentKey}
        componentInstanceKey={componentInstanceKey}
        properties={properties}
        top={top}
        style={{
          aspectRatio: containerRatio
        }}
        className="relative">
        <ImpTracker
          impTrackingT2={() => {
            bannerImpression({
              mod_nm: event_key ?? componentKey,
              mod_pos: componentPosition,
              co: {
                url: video_url
              },
              ctx: trackingContext
            });
          }}
          className="relative w-full h-full">
          <CroppedImage
            url={mobile_background_image}
            wrapperClassName="w-full h-full"
            className="block w-full h-full object-cover"
            showLoadingPlaceholder={false}
            alt=""
            aria-hidden="true"
            lazyLoad={false}
          />
          {isMounted && (
            <AutoPlayVideo
              src={video_url}
              loop
              muted
              preload="auto"
              playsInline
              controls={false}
              className="absolute top-0 left-0 w-full h-full object-cover"
              poster={mobile_background_image}
              aria-hidden="true"
              tabIndex={-1}
            />
          )}
          {!!mobile_foreground_image && (
            <div
              role={isInteractive ? 'button' : undefined}
              tabIndex={tabIndex}
              aria-label={isInteractive ? 'Play video' : undefined}
              className="absolute top-0 left-0 w-full h-full"
              onClick={() => {
                // Close any previous PiP before opening the dialog
                exitAnyPictureInPicture();
                if (isInteractive) {
                  controller.show();
                  bannerClick({
                    mod_nm: event_key ?? componentKey,
                    mod_pos: componentPosition,
                    co: {
                      url: video_url
                    },
                    ctx: trackingContext
                  });
                }
              }}>
              <CroppedImage
                url={mobile_foreground_image}
                wrapperClassName="w-full h-full"
                className="block w-full h-full object-cover"
                showLoadingPlaceholder={false}
                lazyLoad={false}
                alt=""
                aria-hidden="true"
              />
            </div>
          )}
        </ImpTracker>
      </Container>
      {controller.visible && <PlayerDialog src={video_url} controller={controller} />}
    </>
  );
};

export default HeroVideo;
